import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import css from './WorkspaceProjectLists.module.css'
import { useLists } from '../../../../../contexts/ListsContext'
import { useTasks } from '../../../../../contexts/TasksContext'
import { useWorkspace } from '../../../../../contexts/WorkspaceContext'
import { filterTasksByLabels, resolveTaskLabels } from '@/utils/taskLabels'
import { groupTasks, type GroupingMode } from '@/utils/taskGrouping'
import LabelChip from '@/components/ui/LabelChip/LabelChip'
import GroupingSelect from './components/GroupingSelect/GroupingSelect'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { openConfirmDialog } from '../../../../../store/ui/uiSlice'
import { toggleCompleteSaga } from '../../../../../store/tasks/tasksSlice'
import { setScrollToListId } from '../../../../../store/lists/listsSlice'
import Section from '../../../../app/Section/Section'
import { DragDropProvider } from '@dnd-kit/react'
import { move } from '@dnd-kit/helpers'
import { PointerSensor, KeyboardSensor, PointerActivationConstraints } from '@dnd-kit/dom'
import type { DragDropEventHandlers } from '@dnd-kit/react'

const sensors = [
    PointerSensor.configure({
        activatorElements(source) {
            if (!source.handle) return []
            return [source.handle]
        },
        activationConstraints(event: PointerEvent) {
            if (event.pointerType === 'touch') {
                return [new PointerActivationConstraints.Delay({ value: 250, tolerance: 5 })]
            }
            return [new PointerActivationConstraints.Distance({ value: 5 })]
        },
    }),
    KeyboardSensor,
]

const WorkspaceProjectLists = () => {
    const dispatch = useAppDispatch()
    const { projectId } = useParams<{ projectId: string }>()
    const { lists, loading, updateList, deleteList, transferList, createList } = useLists()
    const { tasks, createTask, createTasks, updateTask, reorderTask, moveTaskToList } = useTasks()

    // Which axis the list is laid out along. Page-local like the label filter: leaving the
    // project drops back to Sections. 'sections' is the default view and keeps its own render
    // path below — it is the only one carrying drag & drop and the section options menu.
    const [groupingMode, setGroupingMode] = useState<GroupingMode>('sections')
    const isGrouped = groupingMode !== 'sections'

    const sortedLists = useMemo(() => [...lists].sort((a, b) => a.order - b.order), [lists])

    // Scroll a just-created section into view once it renders (SCD-159). The saga sets the id
    // after the create; this runs when the section reaches the DOM (sortedLists changed), then
    // clears the flag so it fires only once.
    const scrollToListId = useAppSelector(state => state.lists.scrollToListId)
    useEffect(() => {
        if (!scrollToListId) return
        const el = document.querySelector(`[data-list-id="${scrollToListId}"]`)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        dispatch(setScrollToListId(null))
    }, [scrollToListId, sortedLists, dispatch])

    // Label filter — page-local, so leaving the project clears it. Held as a list even though
    // only one label can be active today, so multi-select needs no call-site changes.
    const [activeLabelIds, setActiveLabelIds] = useState<string[]>([])
    const { labels: workspaceLabels, members, statuses } = useWorkspace()
    const activeLabels = useMemo(
        () => resolveTaskLabels(activeLabelIds, workspaceLabels),
        [activeLabelIds, workspaceLabels]
    )
    // Resolve before deciding we're filtering: if the active label was just deleted by someone
    // else, the filter would otherwise hide every task with no chip left to clear it.
    const isFiltering = activeLabels.length > 0
    useEffect(() => {
        if (activeLabelIds.length > 0 && activeLabels.length === 0) setActiveLabelIds([])
    }, [activeLabelIds, activeLabels])

    // One label at a time: clicking another chip replaces the filter, clicking the active one clears it.
    const handleLabelClick = useCallback((labelId: string) => {
        setActiveLabelIds(current => (current.includes(labelId) ? [] : [labelId]))
    }, [])

    // Feed for the grouped views, in the sequence the section view uses (section order, then
    // task order inside it) so a group's rows keep a familiar order. Filtered first, so the
    // label filter and the grouping compose instead of competing.
    const groups = useMemo(() => {
        if (groupingMode === 'sections') return []
        const listOrder = new Map(sortedLists.map((list, index) => [list.id, index]))
        const ordered = [...tasks].sort((a, b) => {
            // A task pointing at a section this project doesn't have (a stale snapshot caught
            // mid-move) sorts last instead of being dropped.
            const la = listOrder.get(a.listId) ?? Number.MAX_SAFE_INTEGER
            const lb = listOrder.get(b.listId) ?? Number.MAX_SAFE_INTEGER
            if (la !== lb) return la - lb
            return a.order - b.order
        })
        return groupTasks(
            filterTasksByLabels(ordered, activeLabelIds),
            groupingMode,
            { members, labels: workspaceLabels, statuses }
        )
    }, [groupingMode, tasks, sortedLists, activeLabelIds, members, workspaceLabels, statuses])

    const [items, setItems] = useState<Record<string, string[]>>({})
    const isDraggingRef = useRef(false)
    const snapshot = useRef<Record<string, string[]>>({})
    const itemsRef = useRef(items)

    useEffect(() => {
        itemsRef.current = items
    }, [items])

    useEffect(() => {
        if (isDraggingRef.current) return

        const newItems: Record<string, string[]> = {}
        for (const list of sortedLists) {
            const listTasks = tasks
                .filter(t => t.listId === list.id && !t.isCompleted)
                .sort((a, b) => a.order - b.order)
            newItems[list.id] = listTasks.map(t => t.id)
        }
        setItems(newItems)
    }, [tasks, sortedLists])

    // Description prefetch for the visible tasks now lives in Section (so every task list warms
    // uniformly) — see Section.tsx.

    const handleDragStart = useCallback<DragDropEventHandlers['onDragStart']>(() => {
        isDraggingRef.current = true
        snapshot.current = structuredClone(itemsRef.current)
    }, [])

    const handleDragOver = useCallback<DragDropEventHandlers['onDragOver']>((event) => {
        const { source } = event.operation
        if (source?.type === 'list') return
        setItems(items => move(items, event))
    }, [])

    const handleDragEnd = useCallback<DragDropEventHandlers['onDragEnd']>((event) => {
        if (event.canceled) {
            isDraggingRef.current = false
            setItems(snapshot.current)
            return
        }

        const taskId = event.operation.source?.id as string
        if (!taskId) {
            isDraggingRef.current = false
            return
        }

        const currentItems = itemsRef.current

        let newListId: string | null = null
        let newIndex = 0

        for (const [listId, taskIds] of Object.entries(currentItems)) {
            const idx = taskIds.indexOf(taskId)
            if (idx !== -1) {
                newListId = listId
                newIndex = idx
                break
            }
        }

        if (!newListId) {
            isDraggingRef.current = false
            return
        }

        let oldListId: string | null = null
        for (const [listId, taskIds] of Object.entries(snapshot.current)) {
            if (taskIds.includes(taskId)) {
                oldListId = listId
                break
            }
        }

        if (newListId === oldListId) {
            reorderTask(taskId, newIndex)
            isDraggingRef.current = false
        } else {
            moveTaskToList(taskId, newListId, newIndex)
            requestAnimationFrame(() => {
                isDraggingRef.current = false
            })
        }
    }, [reorderTask, moveTaskToList])

    if (loading) {
        return null
    }

    const handleMoveList = async (listIndex: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? listIndex - 1 : listIndex + 1
        if (targetIndex < 0 || targetIndex >= sortedLists.length) return

        const currentList = sortedLists[listIndex]
        const targetList = sortedLists[targetIndex]

        await Promise.all([
            updateList(currentList.id, { order: targetList.order }),
            updateList(targetList.id, { order: currentList.order })
        ])
    }

    const handleDeleteList = (listId: string) => {
        const list = lists.find(l => l.id === listId)
        const listName = list?.name || 'Untitled'

        dispatch(openConfirmDialog({
            title: 'Delete list',
            text: `Are you sure you want to delete "${listName}"? This will also delete all tasks in this list. This cannot be undone.`,
            confirmButtonTitle: 'Delete',
            confirmButtonAction: async () => {
                await deleteList(listId)
            }
        }))
    }

    const handleListUpdate = async (listId: string, name: string) => {
        await updateList(listId, { name })
    }

    const handleAddTask = async (listId: string, name: string) => {
        await createTask(listId, name)
    }

    const handleAddTasks = (listId: string, names: string[]) => {
        createTasks(listId, names)
    }

    const handleToggleComplete = (taskId: string, isCompleted: boolean) => {
        dispatch(toggleCompleteSaga({ taskId, isCompleted }))
    }

    const handleAssign = async (taskId: string, assigneeId: string | null) => {
        await updateTask(taskId, { assigneeId })
    }

    const handleTransferList = async (listId: string, newProjectId: string) => {
        await transferList(listId, newProjectId)
    }

    // afterListId set → created from a section's menu (insert right below it); omitted → from the
    // project menu (append). Either way the saga flags the new section to scroll into view.
    const handleAddSection = (afterListId?: string) => {
        if (!projectId) return
        createList(projectId, '', afterListId)
    }

    const getListTasks = (listId: string) => {
        const taskOrder = items[listId] ?? []
        const taskMap = new Map(tasks.map(t => [t.id, t]))

        const orderedOpen = taskOrder
            .map(id => taskMap.get(id))
            .filter((t): t is NonNullable<typeof t> => !!t && !t.isCompleted)

        const inItems = new Set(taskOrder)
        const extraOpen = tasks.filter(t => t.listId === listId && !t.isCompleted && !inItems.has(t.id))

        const completedTasks = tasks.filter(t => t.listId === listId && t.isCompleted)

        return filterTasksByLabels([...orderedOpen, ...extraOpen, ...completedTasks], activeLabelIds)
    }

    // While a filter is active, a section with nothing left to show is hidden rather than
    // rendered as an empty heading — otherwise a large project becomes a wall of empty titles.
    // Each entry keeps its index in sortedLists so reordering stays correct while filtered.
    const visibleLists = isGrouped ? [] : sortedLists
        .map((list, index) => ({ list, index }))
        .filter(({ list }) => !isFiltering || getListTasks(list.id).length > 0)

    // `index` is the section's position in sortedLists, NOT in the filtered list that's being
    // rendered — Move up/down and the dnd list index must address the real section order, or a
    // reorder while filtered would swap the wrong two sections.
    const renderSection = (list: typeof sortedLists[number], index: number) => (
        <Section
            key={list.id}
            listId={list.id}
            listIndex={index}
            title={list.name}
            onTitleUpdate={(name) => handleListUpdate(list.id, name)}
            onDeleteSection={() => handleDeleteList(list.id)}
            onMoveUp={index > 0 ? () => handleMoveList(index, 'up') : undefined}
            onMoveDown={index < sortedLists.length - 1 ? () => handleMoveList(index, 'down') : undefined}
            tasks={getListTasks(list.id)}
            onTaskToggleComplete={handleToggleComplete}
            onTaskAssign={handleAssign}
            // Adding a task while filtered would create one that the filter immediately hides.
            onAddTask={isFiltering ? undefined : (name) => handleAddTask(list.id, name)}
            onAddTasks={isFiltering ? undefined : (names) => handleAddTasks(list.id, names)}
            showCompletedTasks={true}
            draggable={!isFiltering}
            onTransfer={(newProjectId) => handleTransferList(list.id, newProjectId)}
            onAddSection={() => handleAddSection(list.id)}
            currentProjectId={list.projectId}
            onTaskLabelClick={handleLabelClick}
            activeLabelIds={activeLabelIds}
        />
    )

    // A derived group gets no listId and none of the section callbacks, so Section renders it
    // as a bare heading on its non-draggable path: no rename, no options menu, no "+ add task".
    // Grouped is a way to look at the project, not a surface to restructure it from — a task
    // dropped into another group would have to guess a section, and task.order is only
    // meaningful inside one section anyway.
    const renderGroup = (group: typeof groups[number]) => (
        <Section
            key={group.key}
            title={group.title}
            count={group.tasks.length}
            tasks={group.tasks}
            onTaskToggleComplete={handleToggleComplete}
            onTaskAssign={handleAssign}
            showCompletedTasks={true}
            // A group spans sections and task.order only orders within one, so recency is the
            // only meaningful order for its completed rows — same reason My tasks uses it.
            completedSortBy="recent"
            onTaskLabelClick={handleLabelClick}
            activeLabelIds={activeLabelIds}
        />
    )

    const isEmpty = isGrouped ? groups.length === 0 : visibleLists.length === 0

    return (
        <DragDropProvider
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            <div className={css.controls}>
                <GroupingSelect mode={groupingMode} onChange={setGroupingMode} />
                {activeLabels.length > 0 && (
                    <div className={css.filter} data-testid="label-filter">
                        <span className={css.filter_label}>Filtered by</span>
                        {activeLabels.map(label => (
                            <LabelChip
                                key={label.id}
                                name={label.name}
                                color={label.color}
                                onRemove={() => setActiveLabelIds([])}
                                testId="label-filter-chip"
                            />
                        ))}
                    </div>
                )}
            </div>
            <div className={css.lists}>
                {isGrouped
                    ? groups.map(renderGroup)
                    : visibleLists.map(({ list, index }) => renderSection(list, index))}
                {isFiltering && isEmpty && (
                    <div className={css.empty} data-testid="label-filter-empty">
                        <span className={css.empty_text}>No tasks with this label.</span>
                        <button className={css.empty_reset} onClick={() => setActiveLabelIds([])}>
                            Clear filter
                        </button>
                    </div>
                )}
                {/* Grouping an empty project yields no groups at all. Without this the page is a
                    blank area under the control, with no way to add a task from here (adding
                    lives in the section view) and nothing saying why. */}
                {!isFiltering && isGrouped && isEmpty && (
                    <div className={css.empty} data-testid="grouping-empty">
                        <span className={css.empty_text}>No tasks to group.</span>
                        <button className={css.empty_reset} onClick={() => setGroupingMode('sections')}>
                            Back to sections
                        </button>
                    </div>
                )}
            </div>
        </DragDropProvider>
    )
}

export default WorkspaceProjectLists
